import { create } from 'zustand';
import { getTournaments } from '@/api/tournament/tournamentApi';
import type { Tournament } from '@/types/tournament.model';
import { TOURNAMENT_FIELDS } from '@/api/tournament/tournament-fields.config';

interface TournamentState {
  tournaments: Tournament[];
  isLoading: boolean;
  error: string | null;
  fetchTournaments: () => Promise<void>;
  refreshTournaments: () => Promise<void>;
  clearTournaments: () => void;
  isInitialized: boolean;
}

// Bumped by `clearTournaments`, which is how a session switch drops this cache
// (see `lib/session-caches.ts`). A fetch already in flight belongs to the
// session that asked for it, and the 500 ms floor below keeps that window wide
// open: without this it would write one account's tournaments into the sidebar
// of the account that replaced it.
let generation = 0;

export const useTournamentStore = create<TournamentState>((set, get) => ({
  tournaments: [],
  isLoading: false,
  error: null,
  isInitialized: false,
  fetchTournaments: async () => {
    const state = get();

    // Prevent duplicate requests
    if (state.isLoading || state.isInitialized) {
      return;
    }

    const requestGeneration = generation;
    set({ isLoading: true, error: null });
    try {
      const startTime = Date.now();
      const response = await getTournaments(TOURNAMENT_FIELDS.LIST);
      const endTime = Date.now();
      const loadTime = endTime - startTime;

      // Ensure a minimum loading time of 500ms for better UX
      if (loadTime < 500) {
        await new Promise((resolve) => setTimeout(resolve, 500 - loadTime));
      }

      if (requestGeneration !== generation) return;
      set({
        tournaments: response.content,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error) {
      console.error('Error getting tournaments', error);
      if (requestGeneration !== generation) return;
      set({ error: 'Failed to fetch tournaments', isLoading: false });
    }
  },
  clearTournaments: () => {
    generation += 1;
    set({
      tournaments: [],
      isLoading: false,
      error: null,
      isInitialized: false,
    });
  },
  refreshTournaments: async () => {
    const requestGeneration = generation;
    set({ isLoading: true, error: null });
    try {
      const startTime = Date.now();
      const response = await getTournaments(TOURNAMENT_FIELDS.LIST);
      const endTime = Date.now();
      const loadTime = endTime - startTime;

      // Ensure a minimum loading time of 500ms for better UX
      if (loadTime < 500) {
        await new Promise((resolve) => setTimeout(resolve, 500 - loadTime));
      }

      if (requestGeneration !== generation) return;
      set({
        tournaments: response.content,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error) {
      console.error('Error refreshing tournaments', error);
      if (requestGeneration !== generation) return;
      set({ error: 'Failed to refresh tournaments', isLoading: false });
    }
  },
}));
